import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "../../components/layout/PageHeader";
import ConfirmDialog from "../../components/layout/ConfirmDialog";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import {
  CompanyProfile,
  RoundOffMode,
  settingsAPI,
} from "../../services/settingsAPI";
import financialYearAPI, {
  formatFinancialYearDisplay,
} from "../../services/financialYearAPI";
import { companyAPI } from "../../services/companyAPI";
import { useAuthStore } from "../../stores/authStore";

const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: "₹",
  AED: "AED",
  SAR: "SAR",
  USD: "$",
  EUR: "€",
  GBP: "£",
  CAD: "C$",
  AUD: "A$",
  SGD: "S$",
  QAR: "QAR",
  OMR: "OMR",
  KWD: "KD",
  BHD: "BD"
};

const COUNTRY_PRESETS: Record<string, { currencyCode: string; currencySymbol: string; taxRegime: "GST" | "VAT" | "SALES_TAX" | "CUSTOM"; taxLabel: string; taxNumberLabel: string }> = {
  IN: { currencyCode: "INR", currencySymbol: "₹", taxRegime: "GST", taxLabel: "GST", taxNumberLabel: "GSTIN" },
  AE: { currencyCode: "AED", currencySymbol: "AED", taxRegime: "VAT", taxLabel: "VAT", taxNumberLabel: "TRN" },
  SA: { currencyCode: "SAR", currencySymbol: "SAR", taxRegime: "VAT", taxLabel: "VAT", taxNumberLabel: "VAT Number" },
  US: { currencyCode: "USD", currencySymbol: "$", taxRegime: "SALES_TAX", taxLabel: "Sales Tax", taxNumberLabel: "Tax ID / EIN" },
  GB: { currencyCode: "GBP", currencySymbol: "£", taxRegime: "VAT", taxLabel: "VAT", taxNumberLabel: "VAT Reg No" },
  EU: { currencyCode: "EUR", currencySymbol: "€", taxRegime: "VAT", taxLabel: "VAT", taxNumberLabel: "VAT ID" },
  CA: { currencyCode: "CAD", currencySymbol: "C$", taxRegime: "GST", taxLabel: "GST/HST", taxNumberLabel: "Business No" },
  AU: { currencyCode: "AUD", currencySymbol: "A$", taxRegime: "GST", taxLabel: "GST", taxNumberLabel: "ABN" },
  SG: { currencyCode: "SGD", currencySymbol: "S$", taxRegime: "GST", taxLabel: "GST", taxNumberLabel: "GST Reg No" },
  QA: { currencyCode: "QAR", currencySymbol: "QAR", taxRegime: "CUSTOM", taxLabel: "Tax", taxNumberLabel: "Tax ID" },
  OM: { currencyCode: "OMR", currencySymbol: "OMR", taxRegime: "VAT", taxLabel: "VAT", taxNumberLabel: "VAT Reg No" },
  KW: { currencyCode: "KWD", currencySymbol: "KD", taxRegime: "CUSTOM", taxLabel: "Tax", taxNumberLabel: "Tax ID" },
  BH: { currencyCode: "BHD", currencySymbol: "BD", taxRegime: "VAT", taxLabel: "VAT", taxNumberLabel: "VAT Account" }
};

const emptyCompanyProfile = (): CompanyProfile => ({
  legalName: "",
  gstin: "",
  pan: "",
  stateCode: "",
  businessType: "ALL_IN_ONE",
  dlNumber: "",
  fssaiNumber: "",
  country: "IN",
  currencyCode: "INR",
  currencySymbol: "₹",
  currencyPosition: "prefix",
  currencyDecimals: 2,
  taxRegime: "GST",
  taxLabel: "GST",
  taxNumberLabel: "GSTIN"
});

type SettingsTab =
  | "company"
  | "financial"
  | "tax"
  | "billing"
  | "inventory"
  | "nfc"
  | "security";

const SettingsPage: React.FC = () => {
  const { confirm, dialogProps: confirmDialogProps } = useConfirmDialog();
  const queryClient = useQueryClient();
  const authUser = useAuthStore((s) => s.user);
  const companyId = authUser?.companyId || null;
  const [tab, setTab] = useState<SettingsTab>("company");
  const [amount, setAmount] = useState("");
  const [company, setCompany] = useState<CompanyProfile>(emptyCompanyProfile);
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);
  const [defaultTaxInclusive, setDefaultTaxInclusive] = useState(false);
  const [roundOffMode, setRoundOffMode] = useState<RoundOffMode>("none");
  const [maxDiscountPercent, setMaxDiscountPercent] = useState("0");
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState("0");
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["settings"],
    queryFn: () => settingsAPI.getSettings(),
  });

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    companyAPI
      .getById(companyId)
      .then((c) => {
        if (cancelled) return;
        setCompany({
          legalName: c.name || "",
          gstin: c.gstin || "",
          pan: c.pan || "",
          stateCode: c.stateCode || "",
          businessType: c.businessType || "ALL_IN_ONE",
          dlNumber: c.dlNumber || "",
          fssaiNumber: c.fssaiNumber || "",
          country: c.country || "IN",
          currencyCode: c.currencyCode || "INR",
          currencySymbol: c.currencySymbol || "₹",
          currencyPosition: (c.currencyPosition as "prefix" | "suffix") || "prefix",
          currencyDecimals: c.currencyDecimals ?? 2,
          taxRegime: (c.taxRegime as "GST" | "VAT" | "SALES_TAX" | "CUSTOM") || "GST",
          taxLabel: c.taxLabel || "GST",
          taxNumberLabel: c.taxNumberLabel || "GSTIN",
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const { data: financialYears = [], isLoading: fyLoading } = useQuery({
    queryKey: ["financialYears"],
    queryFn: () => financialYearAPI.list(),
  });

  const fyAction = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "close" | "reopen" }) =>
      action === "close"
        ? financialYearAPI.close(id)
        : financialYearAPI.reopen(id),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["financialYears"] });
      setAlert({
        type: "success",
        message:
          vars.action === "close"
            ? "Financial year closed — posting locked"
            : "Financial year reopened",
      });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message:
          err.response?.data?.message ||
          err.message ||
          "FY action failed",
      });
    },
  });

  useEffect(() => {
    if (data != null) {
      setAmount(String(data.cardSecurityAmount ?? 0));
      const prof = data.companyProfile ?? emptyCompanyProfile();
      setCompany(prof);
      if (prof.businessType) {
        localStorage.setItem("business_type", prof.businessType);
        const currentUser = useAuthStore.getState().user;
        if (currentUser) {
          useAuthStore.getState().updateUser({
            company: {
              ...(currentUser.company || { id: companyId || "default", name: prof.legalName || "My Company" }),
              businessType: prof.businessType,
              dlNumber: prof.dlNumber,
              fssaiNumber: prof.fssaiNumber,
            },
          });
        }
      }
      setAllowNegativeStock(Boolean(data.allowNegativeStock));
      setDefaultTaxInclusive(Boolean(data.defaultTaxInclusive));
      setRoundOffMode(data.roundOffMode || "none");
      setMaxDiscountPercent(String(data.maxDiscountPercent ?? 0));
      setSessionTimeoutMinutes(String(data.sessionTimeoutMinutes ?? 0));
    }
  }, [data, companyId]);

  const { mutate: saveSettings, isPending: saving } = useMutation({
    mutationFn: async () => {
      if (companyId) {
        try {
          await companyAPI.update(companyId, {
            name: company.legalName,
            gstin: company.gstin,
            pan: company.pan,
            stateCode: company.stateCode,
            businessType: company.businessType,
            dlNumber: company.dlNumber,
            fssaiNumber: company.fssaiNumber,
            country: company.country,
            currencyCode: company.currencyCode,
            currencySymbol: company.currencySymbol,
            currencyPosition: company.currencyPosition,
            currencyDecimals: company.currencyDecimals,
            taxRegime: company.taxRegime,
            taxLabel: company.taxLabel,
            taxNumberLabel: company.taxNumberLabel,
          });
        } catch (err) {
          console.warn("[SettingsPage] companyAPI update error:", err);
        }
      }

      if (company.businessType) {
        localStorage.setItem("business_type", company.businessType);
      }
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        useAuthStore.getState().updateUser({
          company: {
            ...(currentUser.company || { id: companyId || "default", name: company.legalName || "My Company" }),
            businessType: company.businessType,
            dlNumber: company.dlNumber,
            fssaiNumber: company.fssaiNumber,
            gstin: company.gstin,
            pan: company.pan,
            country: company.country,
            currencyCode: company.currencyCode,
            currencySymbol: company.currencySymbol,
            taxRegime: company.taxRegime,
            taxLabel: company.taxLabel,
            taxNumberLabel: company.taxNumberLabel,
          },
        });
      }

      return settingsAPI.updateSettings({
        cardSecurityAmount: Number(amount) || 0,
        companyProfile: company,
        allowNegativeStock,
        defaultTaxInclusive,
        roundOffMode,
        maxDiscountPercent: Number(maxDiscountPercent) || 0,
        sessionTimeoutMinutes: Number(sessionTimeoutMinutes) || 0,
      });
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      setAmount(String(updated.cardSecurityAmount));
      if (updated.companyProfile && !companyId) {
        setCompany(updated.companyProfile);
      }
      if (updated.allowNegativeStock !== undefined) {
        setAllowNegativeStock(Boolean(updated.allowNegativeStock));
      }
      if (updated.defaultTaxInclusive !== undefined) {
        setDefaultTaxInclusive(Boolean(updated.defaultTaxInclusive));
      }
      if (updated.roundOffMode) setRoundOffMode(updated.roundOffMode);
      if (updated.maxDiscountPercent !== undefined) {
        setMaxDiscountPercent(String(updated.maxDiscountPercent));
      }
      if (updated.sessionTimeoutMinutes !== undefined) {
        setSessionTimeoutMinutes(String(updated.sessionTimeoutMinutes));
      }
      setAlert({ type: "success", message: "Settings saved successfully." });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to save settings",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAlert(null);
    const value = Number(amount);
    if (!Number.isFinite(value) || value < 0) {
      setAlert({ type: "error", message: "Enter a valid amount (0 or greater)." });
      return;
    }
    saveSettings();
  };

  const updateCompany = (field: keyof CompanyProfile, value: string) => {
    setCompany((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Box>
      <PageHeader
        title="Settings"
        subtitle="System configuration (Super Admin only)"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Settings" },
        ]}
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || "Failed to load settings"}
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          <Tabs
            value={tab}
            onChange={(_e, value: SettingsTab) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab value="company" label="Company & GST" />
            <Tab value="financial" label="Financial year" />
            <Tab value="tax" label="Tax" />
            <Tab value="billing" label="Billing" />
            <Tab value="inventory" label="Inventory" />
            <Tab value="nfc" label="NFC security" />
            <Tab value="security" label="Security" />
          </Tabs>

          {tab === "company" && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Company / GST profile
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {companyId
                  ? "Saved on your company profile (used on GST reports and invoices)."
                  : "Used on GST reports, invoices, and Tally export. Stored in app settings."}
              </Typography>

              <Grid container spacing={2} maxWidth={720}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Legal name"
                    value={company.legalName}
                    onChange={(e) => updateCompany("legalName", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="business-type-label">Trade / Business Vertical</InputLabel>
                    <Select
                      labelId="business-type-label"
                      label="Trade / Business Vertical"
                      value={company.businessType || "ALL_IN_ONE"}
                      onChange={(e) => updateCompany("businessType", e.target.value)}
                    >
                      <MenuItem value="ALL_IN_ONE">All-in-One / General Hybrid (All Modules)</MenuItem>
                      <MenuItem value="RESTAURANT">Restaurant / Cafe / Food (Tables & Recipes)</MenuItem>
                      <MenuItem value="GROCERY">Grocery / Supermarket (Barcode Retail POS)</MenuItem>
                      <MenuItem value="PHARMACY">Pharmacy / Medical Store (D.L. & Expiry)</MenuItem>
                      <MenuItem value="WHOLESALE">Wholesale / Distribution (Bulk Trade)</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    Non-restaurant modes (Grocery, Pharmacy, Wholesale) hide Tables and Recipes from the navigation menu.
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Drug License No. (D.L. No.)"
                    value={company.dlNumber || ""}
                    onChange={(e) => updateCompany("dlNumber", e.target.value)}
                    placeholder="e.g. 20B/21B-MH-12345"
                    helperText="Required for Pharmacy / Medical stores (printed on bills)"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="FSSAI License No."
                    value={company.fssaiNumber || ""}
                    onChange={(e) => updateCompany("fssaiNumber", e.target.value)}
                    placeholder="e.g. 10019022009876"
                    helperText="Required for Food, Restaurant & Grocery businesses"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="country-label">Country</InputLabel>
                    <Select
                      labelId="country-label"
                      label="Country"
                      value={company.country || "IN"}
                      onChange={(e) => {
                        const cCode = e.target.value;
                        const preset = COUNTRY_PRESETS[cCode];
                        if (preset) {
                          setCompany((prev) => ({
                            ...prev,
                            country: cCode,
                            currencyCode: preset.currencyCode,
                            currencySymbol: preset.currencySymbol,
                            taxRegime: preset.taxRegime,
                            taxLabel: preset.taxLabel,
                            taxNumberLabel: preset.taxNumberLabel,
                          }));
                        } else {
                          updateCompany("country", cCode);
                        }
                      }}
                    >
                      <MenuItem value="IN">India (IN)</MenuItem>
                      <MenuItem value="AE">United Arab Emirates (AE)</MenuItem>
                      <MenuItem value="SA">Saudi Arabia (SA)</MenuItem>
                      <MenuItem value="US">United States (US)</MenuItem>
                      <MenuItem value="GB">United Kingdom (GB)</MenuItem>
                      <MenuItem value="EU">European Union (EU)</MenuItem>
                      <MenuItem value="CA">Canada (CA)</MenuItem>
                      <MenuItem value="AU">Australia (AU)</MenuItem>
                      <MenuItem value="SG">Singapore (SG)</MenuItem>
                      <MenuItem value="QA">Qatar (QA)</MenuItem>
                      <MenuItem value="OM">Oman (OM)</MenuItem>
                      <MenuItem value="KW">Kuwait (KW)</MenuItem>
                      <MenuItem value="BH">Bahrain (BH)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel id="currency-label">Currency</InputLabel>
                    <Select
                      labelId="currency-label"
                      label="Currency"
                      value={company.currencyCode || "INR"}
                      onChange={(e) => {
                        const code = e.target.value;
                        const sym = CURRENCY_SYMBOLS[code] || code;
                        setCompany((prev) => ({
                          ...prev,
                          currencyCode: code,
                          currencySymbol: sym,
                        }));
                      }}
                    >
                      <MenuItem value="INR">INR (₹)</MenuItem>
                      <MenuItem value="AED">AED (AED)</MenuItem>
                      <MenuItem value="SAR">SAR (SAR)</MenuItem>
                      <MenuItem value="USD">USD ($)</MenuItem>
                      <MenuItem value="EUR">EUR (€)</MenuItem>
                      <MenuItem value="GBP">GBP (£)</MenuItem>
                      <MenuItem value="CAD">CAD (C$)</MenuItem>
                      <MenuItem value="AUD">AUD (A$)</MenuItem>
                      <MenuItem value="SGD">SGD (S$)</MenuItem>
                      <MenuItem value="QAR">QAR (QAR)</MenuItem>
                      <MenuItem value="OMR">OMR (OMR)</MenuItem>
                      <MenuItem value="KWD">KWD (KD)</MenuItem>
                      <MenuItem value="BHD">BHD (BD)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel id="tax-regime-label">Tax Regime</InputLabel>
                    <Select
                      labelId="tax-regime-label"
                      label="Tax Regime"
                      value={company.taxRegime || "GST"}
                      onChange={(e) => {
                        const reg = e.target.value as "GST" | "VAT" | "SALES_TAX" | "CUSTOM";
                        let label = "GST";
                        let numLabel = "GSTIN";
                        if (reg === "VAT") {
                          label = "VAT";
                          numLabel = "TRN / VAT ID";
                        } else if (reg === "SALES_TAX") {
                          label = "Sales Tax";
                          numLabel = "Tax ID";
                        } else if (reg === "CUSTOM") {
                          label = "Tax";
                          numLabel = "Tax ID";
                        }
                        setCompany((prev) => ({
                          ...prev,
                          taxRegime: reg,
                          taxLabel: label,
                          taxNumberLabel: numLabel,
                        }));
                      }}
                    >
                      <MenuItem value="GST">GST</MenuItem>
                      <MenuItem value="VAT">VAT</MenuItem>
                      <MenuItem value="SALES_TAX">Sales Tax</MenuItem>
                      <MenuItem value="CUSTOM">Custom / Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={company.taxNumberLabel || "GSTIN / Tax ID"}
                    value={company.gstin}
                    onChange={(e) => updateCompany("gstin", e.target.value.toUpperCase())}
                    placeholder={company.taxRegime === "VAT" ? "e.g. 100234567890003" : "e.g. 07AAAAA0000A1Z5"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="PAN"
                    value={company.pan}
                    onChange={(e) => updateCompany("pan", e.target.value.toUpperCase())}
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="State code"
                    value={company.stateCode}
                    onChange={(e) => updateCompany("stateCode", e.target.value)}
                    inputProps={{ maxLength: 2 }}
                    helperText="e.g. 27 for Maharashtra"
                  />
                </Grid>
              </Grid>

              <Box mt={3}>
                <Button
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={saving}
                  onClick={() => {
                    setAlert(null);
                    saveSettings();
                  }}
                >
                  {saving ? "Saving…" : "Save company profile"}
                </Button>
              </Box>
            </CardContent>
          </Card>
          )}

          {tab === "financial" && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Financial year
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Close a year to lock new bill / purchase / recharge / voucher
                numbers for that FY. Super Admin only.
              </Typography>
              {fyLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Year</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {financialYears.map((fy) => (
                      <TableRow key={fy.id}>
                        <TableCell>
                          {formatFinancialYearDisplay({
                            ...fy,
                            isClosed: false,
                          })}
                          {fy.isCurrent ? (
                            <Chip
                              size="small"
                              label="Current"
                              color="primary"
                              sx={{ ml: 1 }}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {fy.startDate} → {fy.endDate}
                        </TableCell>
                        <TableCell>
                          {fy.isClosed ? (
                            <Chip size="small" label="Closed" color="warning" />
                          ) : (
                            <Chip size="small" label="Open" color="success" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {fy.isClosed ? (
                            <Button
                              size="small"
                              disabled={fyAction.isPending}
                              onClick={async () => {
                                const ok = await confirm({
                                  title: "Reopen financial year",
                                  message: `Reopen ${fy.code}? Posting will be allowed again.`,
                                  confirmLabel: "Reopen",
                                  tone: "warning",
                                });
                                if (!ok) return;
                                fyAction.mutate({
                                  id: fy.id,
                                  action: "reopen",
                                });
                              }}
                            >
                              Reopen
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              color="warning"
                              disabled={fyAction.isPending}
                              onClick={async () => {
                                const ok = await confirm({
                                  title: "Close financial year",
                                  message: `Close ${fy.code}? New documents for this FY will be blocked.`,
                                  confirmLabel: "Close",
                                  tone: "warning",
                                });
                                if (!ok) return;
                                fyAction.mutate({
                                  id: fy.id,
                                  action: "close",
                                });
                              }}
                            >
                              Close
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          )}

          {tab === "tax" && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Tax defaults
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Company-wide defaults for new products and bill totals. Sales-point
                tax mode (CGST/SGST vs IGST) still overrides per counter.
              </Typography>
              <Stack spacing={2} maxWidth={480}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={defaultTaxInclusive}
                      onChange={(e) => setDefaultTaxInclusive(e.target.checked)}
                    />
                  }
                  label="Default prices are tax-inclusive"
                />
                <FormControl fullWidth>
                  <InputLabel id="round-off-label">Bill round-off</InputLabel>
                  <Select
                    labelId="round-off-label"
                    label="Bill round-off"
                    value={roundOffMode}
                    onChange={(e) =>
                      setRoundOffMode(e.target.value as RoundOffMode)
                    }
                  >
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="0.05">Nearest {company.currencySymbol || "₹"}0.05</MenuItem>
                    <MenuItem value="1.00">Nearest {company.currencySymbol || "₹"}1</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={saving}
                  onClick={() => saveSettings()}
                >
                  {saving ? "Saving…" : "Save tax settings"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
          )}

          {tab === "billing" && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Billing policy
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Caps and policies for POS discounts. Electron can also enforce a
                discount PIN threshold separately.
              </Typography>
              <Stack spacing={2} maxWidth={420}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max discount (%)"
                  value={maxDiscountPercent}
                  onChange={(e) => setMaxDiscountPercent(e.target.value)}
                  inputProps={{ min: 0, max: 100, step: "0.01" }}
                  helperText="0 = no company-wide cap recorded (PIN rules still apply on POS)"
                />
                <Button
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={saving}
                  onClick={() => saveSettings()}
                >
                  {saving ? "Saving…" : "Save billing settings"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
          )}

          {tab === "inventory" && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Inventory
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={allowNegativeStock}
                    onChange={(e) => setAllowNegativeStock(e.target.checked)}
                  />
                }
                label="Allow negative stock (disable guard on sale/adjust)"
              />
              <Box mt={3}>
                <Button
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={saving}
                  onClick={() => {
                    setAlert(null);
                    saveSettings();
                  }}
                >
                  {saving ? "Saving…" : "Save inventory settings"}
                </Button>
              </Box>
            </CardContent>
          </Card>
          )}

          {tab === "nfc" && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                NFC card security
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                When a physical card receives its first recharge, this amount is deducted once
                as a security deposit. Two ledger entries are created: recharge credit and
                security debit. The card will not be charged again on later recharges.
              </Typography>

              <Box component="form" onSubmit={handleSubmit} maxWidth={420}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Card security amount (₹)"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  inputProps={{ min: 0, step: "0.01" }}
                  helperText="Set to 0 to disable security deduction"
                />
                <Box mt={3}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={
                      saving ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save settings"}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
          )}

          {tab === "security" && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Session security
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Website idle timeout for Super Admin sessions. POS auto-lock is
                configured in Electron Settings → Security. Two-factor auth for
                Super Admin is under Profile → Two-factor auth.
              </Typography>
              <Stack spacing={2} maxWidth={420}>
                <TextField
                  fullWidth
                  type="number"
                  label="Session timeout (minutes)"
                  value={sessionTimeoutMinutes}
                  onChange={(e) => setSessionTimeoutMinutes(e.target.value)}
                  inputProps={{ min: 0, max: 1440, step: 1 }}
                  helperText="0 = disabled · max 1440 (24 hours)"
                />
                <Button
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={saving}
                  onClick={() => saveSettings()}
                >
                  {saving ? "Saving…" : "Save security settings"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
          )}
        </Stack>
      )}

      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
};

export default SettingsPage;
